/**
 * 센터 관리 관련 React Query 훅
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { getFirebaseFunctions } from "@/config/firebase";

// --- 로컬 타입 정의 (백엔드 Cloud Functions 요청/응답) ---

/** 센터 목록 아이템 */
export interface CenterListItem {
  centerId: number;
  name: string;
  location: string | null;
  contact: string | null;
  createdAt: string;
  /** 센터 소유자 UID */
  ownerUid: string;
  /** 현재 사용자의 역할 */
  role: "owner" | "admin";
}

interface ListCentersResponse {
  centers: CenterListItem[];
}

interface CreateCenterRequest {
  centerName: string;
  location?: string;
  contact?: string;
}

interface CreateCenterResponse {
  success: true;
  /** 서버에서 발급된 centerId (14자리 이상) */
  centerId: number;
  /** 서버에서 발급된 studioId (5자리 이상) */
  studioId: number;
}

interface UpdateCenterRequest {
  centerId?: number;
  centerName: string;
  location?: string;
  contact?: string;
}

interface UpdateCenterResponse {
  success: true;
}

interface DeleteCenterRequest {
  centerId: number;
}

interface DeleteCenterResponse {
  success: true;
}

/** 센터 생성 시 외부에서 전달하는 파라미터 (centerId 제외) */
export interface CreateCenterParams {
  centerName: string;
  location?: string;
  contact?: string;
}

/** 센터 수정 시 외부에서 전달하는 파라미터 */
export interface UpdateCenterParams {
  centerId?: number;
  centerName: string;
  location?: string;
  contact?: string;
}

/**
 * 센터 목록 조회 훅
 */
export function useCenters() {
  return useQuery({
    queryKey: ["centers"],
    queryFn: async () => {
      const fn = httpsCallable<void, ListCentersResponse>(getFirebaseFunctions(), "listCenters");
      const result = await fn();
      return result.data;
    },
    staleTime: 1000 * 60 * 5, // 5분
  });
}

/**
 * 센터 생성 mutation
 * - centerId / studioId는 서버에서 발급되어 응답으로 반환된다 (Step 4 이후)
 * - 호출자가 후속 처리에 ID가 필요하면 mutation.data.centerId / .studioId로 옵트인
 */
export function useCreateCenter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateCenterParams) => {
      const fn = httpsCallable<CreateCenterRequest, CreateCenterResponse>(
        getFirebaseFunctions(),
        "createCenter",
      );
      const result = await fn(params);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["centers"] });
    },
  });
}

/**
 * 센터 수정 mutation
 */
export function useUpdateCenter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateCenterParams) => {
      const fn = httpsCallable<UpdateCenterRequest, UpdateCenterResponse>(
        getFirebaseFunctions(),
        "updateCenter",
      );
      const result = await fn(params);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["centers"] });
    },
  });
}

/**
 * 센터 삭제 mutation
 */
export function useDeleteCenter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: DeleteCenterRequest) => {
      const fn = httpsCallable<DeleteCenterRequest, DeleteCenterResponse>(
        getFirebaseFunctions(),
        "deleteCenter",
      );
      const result = await fn(request);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["centers"] });
    },
  });
}
